import { ArbitraryObject, traverse } from 'object-traversal';
import { cloneDeep } from '@apollo/client/utilities';
import { useEffect, useState } from 'react';

type UserNameProps = {
  readonly first_name?: string;
  readonly last_name?: string;
};

/**
 * A function that tests for whether `value` is `undefined` and returns a default value if so.
 * @param defaultValue the value to return if `value` is undefined.
 * @param value the value to test for being undefined.
 * @returns value if it exists, otherwise defaultValue.
 */
export const valueOrDefault = (
  defaultValue: string,
  value?: string | null
): string => (value === undefined || value === null ? defaultValue : value);

/**
 * Label to display a user's name based on the provided first and last names.
 * @param user that contains first and last name.
 * @return full name of the user.
 */
export const userFullName = (user: UserNameProps): string => {
  const firstName = user.first_name === undefined ? '' : `${user.first_name} `;

  const fullName = `${firstName}${user.last_name ?? ''}`;

  return fullName.trim();
};

/**
 * Make sure the provided value is a string with value. It should not be empty, with only spaces, null or undefined.
 * @param value that could be non empty string.
 * @returns string value that has at least 1 character that is not space.
 */
export const nonEmptyString = (value: string | null | undefined) => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  return undefined;
};

/**
 * Due to an issue with graphql-zeus where it types the nullable value from graphql response as undefined, but the actual
 * value in the response is null, we need to perform a response transformation to convert all null values to undefined.
 *
 * From graphql side, the best practice is to return 'null' for nullable value so that it is clearly indicate the query executed
 * successfully and it returns the field, and if graphql omits a nullable field from returning, it implies the field did not include
 * in the query at all.
 *
 * So the graphql-zeus should create Stored Types correctly with null instead of undefined. This may be addressed in the future
 * and we may not need this function.
 *
 * @see https://github.com/graphql-editor/graphql-zeus/issues/262
 * @see https://github.com/graphql/graphql-spec/issues/766
 * @param data object data.
 * @returns object data with null value replaced with undefined.
 */
export const objValueNullToUndefined = <T>(data?: ArbitraryObject) => {
  if (data !== undefined) {
    const convertedData = cloneDeep(data);

    traverse(convertedData, ({ value, key, parent }) => {
      if (value === null && parent !== null && key !== null) {
        // eslint-disable-next-line functional/immutable-data
        parent[key] = undefined;
      }
    });

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return convertedData as T;
  }

  return undefined;
};

/**
 * Strips all duplicates out of an array
 * @param data an array of strings
 * @returns the input data stripped of duplicates
 */
export const arrayWithoutDuplicates = (
  data: readonly string[]
): readonly string[] => Array.from(new Set(data));

/**
 * Debounce hook, used for bouncing value, typically for user search text.
 * @param value a possibly bouncing value.
 * @param delay debounce window in millisecond.
 * @returns the last value of debounce window
 */
export function useDebounce<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Partitions an array into two arrays based on a predicate.
 *
 * Items for which the predicate is true will be placed in the first array in the returned tuple,
 * the others will be placed in the second array.
 *
 * @example
 * const [a, b] = partition([1, 2, 3, 4], (n) => n > 2);
 * console.log(a);
 * // [1, 2]
 * console.log(b);
 * // [3, 4]
 *
 * @param array the array to partition
 * @param predicate the function which determines which array each item is placed in
 * @returns a two element tuple
 */
export const partition = function <T>(
  array: readonly T[],
  predicate: (T: T) => boolean
): readonly [readonly T[], readonly T[]] {
  return [
    array.filter((item) => predicate(item)),
    array.filter((item) => !predicate(item)),
  ];
};

/**
 * Any item in the given array that matches the predicate will be placed first
 * in the returned array, otherwise the order is unchanged.
 *
 * The order is stable (e.g. the items retain their existing order after the items are brought to the front)
 *
 * @param array the array to reorder
 * @param predicate determines which items to bring to the front of the array
 * @returns a new array with the matching items at the head
 */
export const bringMatchingItemsToHead = <T>(
  array: readonly T[],
  predicate: (T: T) => boolean
): readonly T[] => {
  const [head, tail] = partition(array, predicate);
  return [...head, ...tail];
};

/**
 * A simple type guard to use with Array.prototype.filter when you want
 * to filter out undefined values from an array in a type safe manner.
 *
 * @example
 *
 * // Type of x is (string | undefined)[]
 * const x = ['a', undefined, 'b', undefined];
 *
 * // Value of y is ['a', 'b']
 * // Type of y is string[]
 * const y = x.filter(isDefined);
 *
 * @param value the possibly undefined value
 * @returns whether the value is defined or not
 */
export const isDefined = <T>(value: T | undefined): value is T =>
  value !== undefined;

/**
 * Determines whether the given value is undefined or null.
 */
export const isNullOrUndefined = <T>(
  value: T | undefined | null
): value is undefined | null => value === undefined || value === null;

/**
 * Filters out all undefined values from an array.
 */
export const compact = <T>(array: readonly (T | undefined)[]): readonly T[] =>
  array.filter(isDefined);

/**
 * Returns the first element of an array or the element itself if it is not an array.
 *
 * @param array - The array to get the first element from.
 * @returns The first element of the array or the element itself if it is not an array.
 */
export const firstOrSelf = <T>(array: T | readonly T[]): T | undefined => {
  return array instanceof Array ? array[0] : array;
};

export const recordOrEmpty = <T>(
  key: string,
  value: T | undefined
): Record<string, T> | undefined =>
  value === undefined ? {} : { [key]: value };

/**
 * Trims a string to the provided length. If the length is greater than than the given max,
 * and an ellipse is specified, it will trim it to 3 less than the max, with an ellipse ('…') appended.
 * @param str - The string to trim.
 * @param length - The length to trim the string at.
 * @param ellipsis - Whether or not to add an ellipse to the end of the string.
 * @returns The trimmed string.
 */
export const truncate = (str: string, length: number, ellipsis = false) =>
  length === 0
    ? ''
    : length === 1 && ellipsis
      ? '…'
      : str.length > length
        ? str
            .slice(0, length - (ellipsis ? 1 : 0))
            .trim()
            .concat(ellipsis ? '…' : '')
        : str;

/**
 * If a string has words that are longer than 40 characters,
 * breaks those words into 40 characters or less,
 * appending a hyphen ('-') after each line.
 *
 * @param text - The string with long words to add breaks into.
 * @returns The string with line breaks inserted.
 */
export const hyphenateLongWords = (text: string) =>
  text.replace(/(\w{40})/gu, '$1-\n');
